/**
 * Cloudflare Turnstile (invisible) — load script once, execute on demand.
 * Env: VITE_TURNSTILE_SITE_KEY (client). Server uses TURNSTILE_SECRET when FF_SIGNUP_TURNSTILE=true.
 */

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

let scriptPromise: Promise<void> | null = null

export function turnstileSiteKey(): string | null {
  const key = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined
  return key?.trim() || null
}

export function turnstileEnabledOnClient(): boolean {
  return !!turnstileSiteKey()
}

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.turnstile) return Promise.resolve()
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src^="${SCRIPT_SRC.split('?')[0]}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      return
    }
    const s = document.createElement('script')
    s.src = SCRIPT_SRC
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('Turnstile script failed to load'))
    document.head.appendChild(s)
  })
  return scriptPromise
}

/** Execute invisible Turnstile; returns token or null if site key missing. */
export async function executeTurnstile(action = 'signup'): Promise<string | null> {
  const siteKey = turnstileSiteKey()
  if (!siteKey) return null
  await loadTurnstileScript()
  if (!window.turnstile) throw new Error('Turnstile unavailable')

  return new Promise((resolve, reject) => {
    const container = document.createElement('div')
    container.style.display = 'none'
    document.body.appendChild(container)
    let widgetId: string | undefined

    const cleanup = () => {
      try {
        if (widgetId != null) window.turnstile?.remove(widgetId)
      } catch { /* ignore */ }
      container.remove()
    }

    widgetId = window.turnstile!.render(container, {
      sitekey: siteKey,
      size: 'invisible',
      action,
      callback: (token: string) => {
        cleanup()
        resolve(token)
      },
      'error-callback': () => {
        cleanup()
        reject(new Error('Security check failed'))
      },
      'expired-callback': () => {
        cleanup()
        reject(new Error('Security check expired — try again'))
      },
    })

    window.turnstile!.execute(widgetId)
  })
}

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string
      execute: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
  }
}
